import {
	BulkDeleteRecordsRequest,
	BulkUpdateRecordsRequest,
	CustomView,
	CustomViewResponse,
	CustomViewsMetadata,
	ListPagePostRequest
} from "@/Contracts/UI/index";
import axios, { AxiosResponse } from "axios";
import { queryCache } from "react-query";

import {
	getRequest,
	postRequest,
	putRequest,
	deleteRequest
} from "@/Utils/Service/BackendService";

import {
	DeletedRecord,
	TableQueryFilter,
	TableQueryFilterOperands,
	ZomentumEntities
} from "@/Contracts";
import {
	Client,
	ClientsRequest,
	ClientsResponse,
	AddClientRequest,
	ClientContactsResponse,
	ClientContact,
	AddClientContactRequest,
	ClientContactRequest,
	EditClientRequest,
	ClientContactBulkUpdateRequest,
	ClientContactBulkUpdateServiceResponse,
	ClientBulkUpdateServiceResponse
} from "@/Contracts/Clients";
import {
	getTableQueryFilters,
	includeChildEntities,
	ChildEntityHydrationParams
} from "@/Contracts/Utils";
import {
	GlobalSearchEntityType,
	GlobalSearchRequest
} from "@/Contracts";
import { getGlobalSearch } from "@/Utils";
import {
	ClientsGlobalSearchRequest,
	ClientsGlobalSearchResponse
} from "@/Contracts";
const getClientsUrl = "/v2/client/companies";
const deleteClientUrl = "/v2/client/companies/{client_id}";
const addClientUrl = "/v2/client/companies";
const editClientUrl = "/v2/client/companies/{client_id}";
const getClientDetailsUrl = "/v2/client/companies/{client_id}";
const getClientContactsUrl = "/v2/client/users";
const getClientContactDetailsUrl = "/v2/client/users/{client_contact_id}";
const deleteClientContactUrl = "/v2/client/users/{client_contact_id}";
const addClientContactUrl = "/v2/client/users";
const editClientContactUrl = "/v2/client/users/{client_contact_id}";
const mergeClientContactUrl =
	"/v2/client/users/{client_contact_id}/merge-with/{merge_client_contact_id}";
const mergeClientUrl =
	"/v2/client/companies/{client_id}/merge-with/{merge_client_id}";

const getAllClientViewURL = "/v2/views?entity_type={entity_type}";
const getClientViewMetadataURL = "/v2/views/metadata?entity_type={entity_type}";

const getClientListPostURL = "/v2/client/companies/view";
const createClientViewURL = "/v2/views";
const updateClientViewURL = "/v2/views/{view_id}";
const deleteClientViewURL = "v2/views/{view_id}";

const bulkActionURL = "/v2/async/operation";

const clientContactBulkUpdateURL = "/v2/client/users/bulk/update/";

export const getClientsUsingGlobalSearch = async (
	clientsGlobalSearchRequest: ClientsGlobalSearchRequest
): Promise<ClientsGlobalSearchResponse> => {
	const globalSearchRequest = new GlobalSearchRequest(
		clientsGlobalSearchRequest.value,
		[GlobalSearchEntityType.Client],
		clientsGlobalSearchRequest.limit ?? 50,
		null,
		true
	);
	try {
		const response = await getGlobalSearch(globalSearchRequest);
		return {
			data: response.data.map(option => option.obj as Client)
		};
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClients = async (
	clientsRequest: ClientsRequest
): Promise<ClientsResponse> => {
	try {
		let queryParams = `${
			clientsRequest.limit && clientsRequest.limit > 0
				? `limit=${clientsRequest.limit}`
				: ""
		}`;

		if (clientsRequest.filters && clientsRequest.filters.length > 0) {
			const filterUrlQuery = getTableQueryFilters(
				clientsRequest.filters,
				false
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				filterUrlQuery && filterUrlQuery.length > 0
					? `${filterUrlQuery}`
					: ""
			}`;
		}

		if (clientsRequest.keywords && clientsRequest.keywords.length > 0) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				clientsRequest.keywords && clientsRequest.keywords.length > 0
					? `q=${clientsRequest.keywords}`
					: ""
			}`;
		}

		if (
			clientsRequest.includeEntities &&
			clientsRequest.includeEntities.length > 0
		) {
			const childEntityUrlQuery = includeChildEntities(
				clientsRequest.includeEntities
			);
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				childEntityUrlQuery && childEntityUrlQuery.length > 0
					? `${childEntityUrlQuery}`
					: ""
			}`;
		}

		if (clientsRequest.sync_source) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.sync_source
					? `sync_source=${clientsRequest.sync_source}`
					: ""
			}`;
		}

		if (clientsRequest.selected_id) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.selected_id
					? `selected_id=${clientsRequest.selected_id}`
					: ""
			}`;
		}

		if (clientsRequest.page) {
			queryParams = `${queryParams}${queryParams.length > 0 ? "&" : ""}${
				!!clientsRequest?.page ? `page=${clientsRequest.page}` : ""
			}`;
		}

		const response: AxiosResponse<ClientsResponse> = await axios(
			getRequest(
				`${getClientsUrl}${
					queryParams.length > 0 ? `?${queryParams}` : ""
				}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteClient = async (client: Client): Promise<DeletedRecord> => {
	queryCache.invalidateQueries("clients");
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteClientUrl.replace("{client_id}", client.id),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addClient = async (
	addClientRequest: AddClientRequest
): Promise<Client> => {
	queryCache.invalidateQueries("clients");
	try {
		const response: AxiosResponse<Client> = await axios(
			postRequest(addClientUrl, addClientRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editClient = async (
	editClientRequest: EditClientRequest
): Promise<Client> => {
	queryCache.invalidateQueries("clients");
	try {
		const includedChildEntities = includeChildEntities(
			editClientRequest?.includedChildEntities
		);
		const response: AxiosResponse<Client> = await axios(
			putRequest(
				editClientUrl.replace("{client_id}", editClientRequest.id) +
					(includedChildEntities && includedChildEntities.length > 0
						? "?" + includedChildEntities
						: ""),
				editClientRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientDetails = async (
	clientId: string,
	includeOpportunities = true,
	includeContacts = true,
	includeDocuments = true,
	includeBlocks = false
): Promise<Client> => {
	try {
		const childEntities: ChildEntityHydrationParams[] = [];

		if (includeOpportunities) {
			childEntities.push(ChildEntityHydrationParams.OPPORTUNITIES_ALL);
		}
		if (includeContacts) {
			childEntities.push(ChildEntityHydrationParams.CLIENT_USERS_ALL);
		}
		if (includeDocuments) {
			childEntities.push(ChildEntityHydrationParams.DOCUMENTS_ALL);
		}
		if (includeBlocks) {
			childEntities.push(ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION);
		}

		const response: AxiosResponse<Client> = await axios(
			getRequest(
                getClientDetailsUrl.replace("{client_id}", clientId) +
                    (childEntities.length > 0
                        ? "?" + includeChildEntities(childEntities)
                        : ""),
                null,
                true,
                false,
                false
            )
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientContacts = async (
	clientContactRequest: ClientContactRequest
): Promise<ClientContactsResponse> => {
	try {
		let url = getClientContactsUrl;
		const appliedFilters = getTableQueryFilters(
			[
				...(!!clientContactRequest.filters.length
					? clientContactRequest.filters
					: []),
				new TableQueryFilter(
					TableQueryFilterOperands.MongoDbEqualTo,
					"client_company_id",
					clientContactRequest.clientId,
					null,
					true
				)
			],
			false
		);
		url =
			url +
			"?" +
			appliedFilters +
			`&page=${clientContactRequest.page}&limit=${clientContactRequest.limit}`;

		if (clientContactRequest.keywords) {
			url = url + `&q=${clientContactRequest.keywords}`;
		}
		if (clientContactRequest.selected_id) {
			url =
				url +
				`&selected_id=${clientContactRequest.selected_id.join(",")}`;
		}
		if (clientContactRequest.countQueryFlag) {
			url += `&count_query_flag=true`;
		}
		if (clientContactRequest.sortQuery) {
			url += `&${clientContactRequest.sortQuery}`;
		}
		if (clientContactRequest.localeQuery) {
			url += `&${clientContactRequest.localeQuery}`;
		}

		if (clientContactRequest.includeChildEntities) {
			const childEntityUrlQuery = includeChildEntities(
				clientContactRequest.includeChildEntities
			);
			url = url + `&${childEntityUrlQuery}`;
		}

		const response: AxiosResponse<ClientContactsResponse> = await axios(
			getRequest(url, null, true, true, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientContactDetails = async (
	clientContactId: string
): Promise<ClientContact> => {
	try {
		const childEntityUrlQuery = includeChildEntities([
			ChildEntityHydrationParams.CLIENT_COMPANIES_NAME
		]);
		const url =
			getClientContactDetailsUrl.replace(
				"{client_contact_id}",
				clientContactId
			) + `?${childEntityUrlQuery}`;
		const response: AxiosResponse<ClientContact> = await axios(
			getRequest(url, null, true, true, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteClientContact = async (
	clientContact: ClientContact
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteClientContactUrl.replace(
					"{client_contact_id}",
					clientContact.id
				),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addClientContact = async (
	addClientContactRequest: AddClientContactRequest
): Promise<ClientContact> => {
	try {
		const response: AxiosResponse<ClientContact> = await axios(
			postRequest(
				addClientContactUrl,
				addClientContactRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// Create a new interface if this service starts using includedChildEntities
export const editClientContact = async (
	clientContact: ClientContact
): Promise<ClientContact> => {
	try {
		const response: AxiosResponse<ClientContact> = await axios(
			putRequest(
				editClientContactUrl.replace(
					"{client_contact_id}",
					clientContact.id
				),
				clientContact,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getClientViewMetadata = async (): Promise<CustomViewsMetadata> => {
	try {
		const response: AxiosResponse<CustomViewsMetadata> = await axios(
			getRequest(
				getClientViewMetadataURL.replace(
					"{entity_type}",
					ZomentumEntities.Client
				),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getAllClientViews = async (): Promise<CustomViewResponse> => {
	try {
		const response: AxiosResponse<CustomViewResponse> = await axios(
			getRequest(
				getAllClientViewURL.replace(
					"{entity_type}",
					ZomentumEntities.Client
				),
				null,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getClientListPost = async (
	clientRequest: ListPagePostRequest
): Promise<ClientsResponse> => {
	try {
		const includedChildEntities = includeChildEntities(
			clientRequest.included_child_entities
		);

		const response: AxiosResponse<ClientsResponse> = await axios(
			postRequest(
				getClientListPostURL + `?${includedChildEntities}`,
				clientRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

// check if there are any includedChilEntitites
export const createClientView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			postRequest(
				createClientViewURL,
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

// check if there are any includedChilEntitites
export const updateClientView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			putRequest(
				updateClientViewURL.replace("{view_id}", customViewRequest.id),
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const deleteClientView = async (
	customViewId: string
): Promise<unknown> => {
	try {
		const response: AxiosResponse<unknown> = await axios(
			deleteRequest(
				deleteClientViewURL.replace("{view_id}", customViewId),
				null,
				true,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const bulkDeleteClients = async (
	bulkDeleteRecordsRequest: BulkDeleteRecordsRequest
): Promise<any> => {
	// fix the type
	try {
		const response: AxiosResponse<any> = await axios(
			postRequest(
				bulkActionURL,
				bulkDeleteRecordsRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const bulkUpdateClients = async (
	bulkUpdateRecordsRequest: BulkUpdateRecordsRequest
): Promise<ClientBulkUpdateServiceResponse> => {
	try {
		const response: AxiosResponse<any> = await axios(
			postRequest(
				bulkActionURL,
				bulkUpdateRecordsRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const bulkUpdateClientContact = async (
	payload: ClientContactBulkUpdateRequest
): Promise<ClientContactBulkUpdateServiceResponse> => {
	try {
		const response: AxiosResponse<ClientContactBulkUpdateServiceResponse> =
			await axios(
				postRequest(
					clientContactBulkUpdateURL,
					payload,
					true,
					false,
					false
				)
			);
		return response.data;
	} catch (err) {
		console.error(err);
		return Promise.reject(err);
	}
};

export const mergeClient = async (
	clientId: string,
	mergeClientId: string
): Promise<Client> => {
	try {
		const response: AxiosResponse<Client> = await axios(
			putRequest(
				mergeClientUrl
					.replace("{client_id}", clientId)
					.replace("{merge_client_id}", mergeClientId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const mergeClientContact = async (
	contactId: string,
	mergeContactId: string
): Promise<Client> => {
	try {
		const response: AxiosResponse<Client> = await axios(
			putRequest(
				mergeClientContactUrl
					.replace("{client_contact_id}", contactId)
					.replace("{merge_client_contact_id}", mergeContactId),
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};
